// #region Imports
import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import { workAgreementListPath } from "work/work-agreements/_views/work-agreement-list-chips";
import WorkVersion from "work/work-agreements/versions/work-version";
import CancelIcon from "@mui/icons-material/Cancel";
import LogoutIcon from "@mui/icons-material/Logout";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import MessageIcon from "@mui/icons-material/Message";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { styled, Theme, ThemeProvider } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import PageTitleBreadcrumb, { breadcrumbHeadingName } from "_shared/_views/breadcrumbs/page-title-breadcrumb";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import {
  actionRowContainment,
  actionRowSeatsOneButton,
} from "_shared/_views/buttons/action-row-layout";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { getForumsByTopic, populateForumsByTopic } from "messaging/forums/_stores/forums-redux-slice";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import useSessionUser from "permissions/session/use-session-user";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import { AccountType } from "users/account-types/account-type";
import { MaskedTeam } from "work/masked-fields";
import { Content, Header, TabContent, TabsContainer } from "work/_views/work-page-styled-components";
import {
  paneFlushContent,
  paneIsCompact,
  workPaneContainment,
  workPaneGutters,
  workPaneHeaderGutters,
} from "work/_views/work-pane-layout";
import useWorkPaneSize, { WorkPaneWidthContext } from "work/_views/use-work-pane-size";
import useWorkPaneTheme from "work/_views/work-pane-typography";
import RecordAccessButton from "work/_views/record-access-button";
import {
  removeWorkAgreementApiCaches,
  useAcceptTeamLeaderHandoverMutation,
  useDeclineTeamLeaderHandoverMutation,
  useEndWorkAgreementMutation,
  useGetWorkAgreementByIdQuery,
  useQuitWorkTeamMutation,
  useRequestTeamLeaderHandoverMutation,
  useReviseWorkAgreementMutation,
  useWithdrawTeamLeaderHandoverMutation,
} from "work/work-agreements/_api/work-agreement-api";
import EntityMember from "_shared/entity-members/entity-member";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/team-leader-handover";
import LeaderHandoverBanner from "work/work-agreements/teams/leader-handover/_views/leader-handover-banner";
import LeaderHandoverDialog, {
  LeaderHandoverChoice,
} from "work/work-agreements/teams/leader-handover/_views/leader-handover-dialog";
import {
  getWorkAgreementById,
  isWorkAgreementLoadIssue,
  removeWorkAgreement,
} from "work/work-agreements/_store/work-agreement-redux-slice";
import { PageTab } from "work/work-agreements/_views/view/view-work-agreement-page-tabs";
import ConflictsTab from "work/work-agreements/_views/view/tabs/view-work-conflicts-tab";
import DetailsTab from "work/work-agreements/_views/view/tabs/view-work-details-tab";
import DiscountTab from "work/work-agreements/_views/view/tabs/view-work-discount-tab";
import FeeScheduleTab from "work/work-agreements/_views/view/tabs/view-work-fee-schedule-tab";
import PoliciesTab from "work/work-agreements/_views/view/tabs/view-work-policies-tab";
import TeamTab from "work/work-agreements/_views/view/tabs/view-work-team-tab";
import ClientTeamTab from "work/work-agreements/_views/view/tabs/view-work-client-team-tab";
import WorkAgreement, { WorkAgreementFieldCategory } from "work/work-agreements/work-agreement";
import ChatDrawer from "work/work-agreements/_views/chat-drawer";
import { ConversationPane } from "messaging/conversations/conversation-links";
import useConversationPaneRequest from "messaging/conversations/use-conversation-pane-request";
import Tooltip from "@mui/material/Tooltip";
import LoadingButton from "_shared/_views/loading/loading-button";
import { HttpStatusCode } from "axios";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useRemoveFromWork } from "work/work-agreements/teams/_views/use-remove-from-work";
import useMediaQuery from "@mui/material/useMediaQuery";
// #endregion


// #region Styled Components
const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));
/**
 * No horizontal padding: the sticky header around it stands in the pane's header gutter for every
 * row it holds — see `PaneBand`. A second inset here put the title 24px further in than the tab
 * strip below it, and its own compact step was the viewport's answer to a question about the pane.
 */
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  gap: theme.spacing(1),
  justifyContent: "space-between",
}));
const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  flexDirection: "row",
  minWidth: 0,
}));
/**
 * The agreement's name, which is as long as whoever created it made it.
 *
 * Ellipsised rather than wrapped: the row it shares with the Correspondence button is the page's
 * first line, and a name that wraps pushes the tab strip down the page. The compact size is a step
 * below what the pane's nested theme already gives an `h4` there — see `work-pane-typography` —
 * because the breadcrumb and the button share this row and the title is the one thing on it that
 * can yield.
 */
const PageTitle = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  padding: theme.spacing(0, 1),
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [paneIsCompact(theme)]: {
    fontSize: "1.25rem",
    paddingLeft: theme.spacing(0.5),
  },
}));
const StickyPageHeader = styled("section")(({ theme }) => ({
  // The page's chrome stands in a gutter of its own rather than the content's — see
  // `PaneBand`. This outranks the pane's own gutter rather than adding to it, so the title, the tab
  // strip and the actions row below all measure from one number.
  ...workPaneHeaderGutters(theme),
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  position: "sticky",
  top: 0,
  zIndex: 20,
  "&::before": {
    backgroundColor: theme.palette.background.default,
    content: '""',
    height: theme.spacing(1),
    left: 0,
    pointerEvents: "none",
    position: "absolute",
    right: 0,
    top: theme.spacing(-1),
  },
}));
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "showMessageDrawer" && prop !== "isMessageDrawerOverlay",
})<{
  open?: boolean;
  isMessageDrawerOverlay?: boolean;
  showMessageDrawer?: boolean;
}>(({ theme, isMessageDrawerOverlay, showMessageDrawer }) => ({
  /**
   * The pane names itself here, and everything drawn inside it lays out against this element's width
   * rather than the window's — see `work-pane-layout`. Its own rules are the one exception: a
   * container cannot query itself, which is why the horizontal gutter is declared for the pane's
   * children (`workPaneGutters`) rather than written here as padding. The vertical padding below is
   * one number at every width, so it has nothing to ask.
   */
  ...workPaneContainment,
  ...workPaneGutters(theme),
  display: "flex",
  height: "100%",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  // Vertical only. The horizontal inset every row stands in is the gutter above, which knows the
  // difference between the page's chrome and a tab's content; a `paddingRight` here as well was the
  // page's own answer to the drawer being open, and the gutter is now the whole of that answer.
  paddingBottom: 0,
  paddingTop: theme.spacing(1),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: showMessageDrawer && !isMessageDrawerOverlay ? "-400px" : 0,
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: "relative",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
      },
    },
  ],
}));
const ButtonContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  flexDirection: "column",
}));
const CorrespondenceButton = styled(IconButton)(({ theme }) => ({
  height: '40px',
  width: '40px',
  paddingLeft: theme.spacing(1),
}));
/**
 * The word under the correspondence icon, which names the panel that button opens.
 *
 * Kept on one line: the label and the title share the header row, and the title is the one thing on
 * it that yields — see `PageTitle`. A label that broke would take a second line from the header at
 * exactly the widths where the row is already tight.
 */
const CorrespondenceButtonLabel = styled(Typography)(() => ({
  fontSize: "0.6em",
  whiteSpace: "nowrap",
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
/**
 * The row of things the reader can do to the agreement. Page chrome, so it stands in the header's
 * gutter alongside the title and the tab strip rather than in the content one.
 */
const ActionsContainer = styled("section")(({ theme }) => ({
  ...workPaneHeaderGutters(theme),
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  paddingBottom: theme.spacing(0),
  paddingTop: theme.spacing(0),
  width: "100%",
}));
/**
 * The notices between the tabs and the action row: an open revision, work the reader has left, a team
 * handover waiting on an answer, an action that did not complete.
 *
 * They stand in the header's gutter, as the action row under them does, so each alert's edge is the
 * edge of the title, the tabs and the buttons at every pane width (#6099). Each alert used to bring a
 * margin of its own on top of the pane's gutter: the handover banners stood 40px in, and the revision
 * banner measured 24px against the window rather than the pane.
 */
const PageNotices = styled("section")(({ theme }) => ({
  ...workPaneHeaderGutters(theme),
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  gap: theme.spacing(1),
  "&:not(:empty)": {
    paddingTop: theme.spacing(1),
  },
}));
/** The row's gap in pixels, which a button also needs to know where two of them stop fitting. */
const actionRowGap = (theme: Theme) => parseFloat(theme.spacing(1));
/**
 * Each button as wide as its label, side by side from the row's start, wrapping only once the next
 * one does not fit — the proposal pages' row (#5973). The row used to deal its buttons into three
 * equal columns, so Revise stretched across a third of the pane and End Agreement stood a column
 * away from it.
 */
const WorkAgreementActions = styled("section")(({ theme }) => ({
  ...actionRowContainment,
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: `${actionRowGap(theme)}px`,
  marginTop: theme.spacing(1),
  width: "100%",
}));
/**
 * Full width once the row is too narrow for two buttons side by side — a single stacked column, where
 * a button that hugs its label reads as misaligned. That is a question about the row, not the pane or
 * the window: the row seats a second button or it does not (#5815).
 */
const stackedActionRow = (theme: Theme) => ({
  [actionRowSeatsOneButton(theme, actionRowGap(theme))]: {
    width: "100%",
  },
});
/** The wrapper a tooltip needs around a button that may be disabled; it sizes to the button. */
const WorkAgreementActionSpan = styled("span")(({ theme }) => ({
  display: "inline-flex",
  ...stackedActionRow(theme),
}));
const WorkAgreementActionButton = styled(LoadingButton)(({ theme }) => ({
  // Wide enough that a one-word action ("Revise") is still a target. See `WorkAgreementActions`.
  minWidth: theme.spacing(15),
  whiteSpace: "nowrap",
  width: "auto",
  ...stackedActionRow(theme),
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));
/**
 * The revision waiting on the reader, which is a state rather than a control.
 *
 * Everything in the action row below is something the reader may do and equally may not: revise the
 * agreement, end it, leave the team. This is the one thing on the page that reports a fact and asks
 * the reader to go and answer it, so it does not belong in that row at all. It sits above the row,
 * directly under the tabs, and it says in a sentence what an open revision means for the agreement
 * the reader is looking at. A control on its own cannot say that: "Review Revision" names the
 * destination and nothing else, and the reader has no way to tell whether the agreement in front of
 * them still holds.
 *
 * The sentence is the same one the Details tab carries beside the link. It is repeated rather than
 * moved, because the Revision field on that tab lists every revision relationship this agreement
 * has -- the one it replaced, the one that replaced it, and this one -- and taking one of the three
 * out of that list would make the list wrong.
 */
const ReviewRequestedBanner = styled(Alert)(() => ({
  alignItems: "center",
  // The sentence wraps; the control must not. Left alone, "Review Revision" breaks across two
  // lines against the banner's right edge and reads as two separate words.
  "& .MuiAlert-action": {
    paddingTop: 0,
  },
  "& .MuiAlert-action .MuiButton-root": {
    whiteSpace: "nowrap",
  },
}));

type WorkAgreementActionRecovery = "end" | "revise" | "quit" | "handover" | "remove";

function getWriteStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || !("status" in error)) return undefined;
  const status = error.status;
  return typeof status === "number" ? status : undefined;
}

function recoveryActionLabel(action: WorkAgreementActionRecovery): string {
  if (action === "end") return "End Agreement";
  if (action === "revise") return "Revise";
  if (action === "handover") return "The team handover";
  if (action === "remove") return "The removal";
  return "Leave Team";
}
/**
 * What Revise says about itself, and why.
 *
 * An agreement carries one revision at a time, and the server refuses a second one — see
 * `WorkAgreement.ReviseAsync` (issue #5559). A button that stays enabled under a banner announcing
 * the revision offers the reader an act that cannot succeed, and each click used to open one more
 * revision of the same agreement. The reason belongs in the tooltip rather than in a snackbar the
 * reader only reads after being refused, and it names where the open revision is: the banner
 * directly above this row carries the way to it.
 */
const REVISE_TOOLTIP =
  "Revise the work agreement to make changes to terms or team members. This will end the " +
  "current agreement and create a new proposal for review.";
const REVISION_IN_PROGRESS_TOOLTIP = "A revision is already in progress. Open it from the banner above.";
// #endregion

export default function ActiveWorkAgreementPage() {
  // #region Hooks
  const session = useSession();
  const sessionUser = useSessionUser();
  const workAgreementAccess = useWhoHasAccess("work-agreements", "the work agreement");
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const { id: idParam, pageTab: pageTabParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * What the page can seat is a question about the pane, not the window — the navigation drawer and
   * the chat drawer between them take 672px the window cannot see, and a docked chat is this page's
   * default.
   */
  const { paneRef, paneWidth } = useWorkPaneSize();
  const paneTheme = useWorkPaneTheme(paneWidth);
  /**
   * The one thing here that really is about the window: whether there is room to seat the chat
   * beside the agreement at all, or whether it has to come over the top of it. Asking the pane would
   * be circular — the drawer is 400px of the answer.
   */
  const isMessageDrawerOverlay = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  // Work Agreement id is required
  if (!idParam) throw new Response("Not Found", { status: HttpStatusCode.NotFound });

  /**
   * What the URL asks of the Correspondence pane: `?pane=correspondence`, and the conversation to open
   * on with `&forum=` (#5826). See `conversation-links`. A request opens the pane however the page is
   * laid out — docked beside the agreement, or as a drawer over it — because a link that opened a page
   * with no chat on it would have gone nowhere.
   */
  const { requestKey: paneRequestKey, forumRequest, forgetRequest: forgetPaneRequest } =
    useConversationPaneRequest(ConversationPane.Correspondence);

  // #region State
  const [messagesPanelOpenState, setMessagesPanelOpenState] = useState({
    docked: true,
    overlay: Boolean(paneRequestKey),
  });
  // Adjusted while rendering, so the page never paints the pane closed first.
  const [honoredPaneRequestKey, setHonoredPaneRequestKey] = useState<string>();
  if (paneRequestKey && paneRequestKey !== honoredPaneRequestKey) {
    setHonoredPaneRequestKey(paneRequestKey);
    setMessagesPanelOpenState({ docked: true, overlay: true });
  }
  const [isEnding, setIsEnding] = useState(false);
  const [isRevising, setIsRevising] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
  const [isAnsweringHandover, setIsAnsweringHandover] = useState(false);
  const [actionRecovery, setActionRecovery] = useState<{
    action: WorkAgreementActionRecovery;
    status: HttpStatusCode.Conflict | HttpStatusCode.PreconditionFailed | "missing-version";
  } | null>(null);
  // #endregion

  // #region Global Variables
  const workAgreementId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const activeTab = useMemo(() => {
    const tab = pageTabParam as PageTab | undefined;
    return tab ?? PageTab.Details;
  }, [pageTabParam]);

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const store = useStore<RootState>();

  // #region Work Agreement Queries
  const workAgreementJson = useSelector(getWorkAgreementById(workAgreementId));
  const workAgreement = useMemo(() => {
    if (!workAgreementJson || isWorkAgreementLoadIssue(workAgreementJson)) return null;
    return WorkAgreement.fromJSON(workAgreementJson);
  }, [workAgreementJson]);

  const workAgreementQuery = useGetWorkAgreementByIdQuery(
    !workAgreementId || workAgreementId.isEmpty
      ? skipToken
      : { workAgreementId: workAgreementId?.value ?? Guid.EMPTY.value, session },
  );
  const workAgreementQueryErrorStatus = getWriteStatus(workAgreementQuery.error);
  const workAgreementIsNoLongerAvailable = workAgreementQuery.isError && (
    workAgreementQueryErrorStatus === HttpStatusCode.Forbidden ||
    workAgreementQueryErrorStatus === HttpStatusCode.NotFound
  );

  // #region Work Agreement Mutations
  const [endWorkAgreement] = useEndWorkAgreementMutation();
  const [reviseWorkAgreement] = useReviseWorkAgreementMutation();
  const [quitWorkTeam] = useQuitWorkTeamMutation();
  const [requestTeamLeaderHandover] = useRequestTeamLeaderHandoverMutation();
  const [acceptTeamLeaderHandover] = useAcceptTeamLeaderHandoverMutation();
  const [declineTeamLeaderHandover] = useDeclineTeamLeaderHandoverMutation();
  const [withdrawTeamLeaderHandover] = useWithdrawTeamLeaderHandoverMutation();

  // #region Messages Queries
  // Not this agreement's id: a revision continues the conversation of the agreement it replaced, so
  // both file their forums under the first agreement in the chain and either page opens the same
  // three rooms. It falls back to this agreement's id, which is what the server sends for one that
  // began its own chain.
  const conversationId = workAgreement?.conversationId ?? workAgreementId;
  const topic = useMemo(() => {
    return Topic.create({
      entityClass: new ForumEntityClass("Work.Agreement"),
      entityId: conversationId,
    });
  }, [conversationId]);
  const forums = useSelector(getForumsByTopic(topic));
  const sortedForums = useMemo(() => {
    return [...(forums ?? [])].sort((left, right) => {
      if (left.name.value === "All Stakeholders") return -1;
      if (right.name.value === "All Stakeholders") return 1;
      return 0;
    });
  }, [forums]);

  // #region Global State
  // A request with nothing to show yet, which is the first load of the page and equally the load of
  // the agreement a revision link sends the reader to. The second is a request the hook does not
  // call its first, so `isLoading` is false for it; without the agreement, the page below has only
  // its own chrome to draw. A refetch of an agreement the page already holds keeps the page, and so
  // does a refetch of the session's grants: an approved access request reads them again (#6596), and
  // swapping the page for a loader then would close the tab the reader asked from.
  const isLoading =
    workAgreementQuery.isLoading ||
    (workAgreementQuery.isFetching && !workAgreement) ||
    (sessionPermissionsLoading && !sessionPermissions);
  const isArchived = useMemo(() => {
    return location.pathname.includes("/archived/");
  }, [location.pathname]);

  /**
   * Whether the signed-in person leads one of this agreement's two teams.
   *
   * Both leaders hold the same authority over the agreement as a whole: `work/{id}/revise` and
   * `work/{id}/end-agreement` each admit `WorkAgreement.Client` and `WorkAgreement.Vendor`, which
   * are the two teams' leaders. The action is attributed to the Legal Entity the user is acting
   * for, so the leader's user and entity must both match. A counterparty's team arrives as `Masked`
   * when the reader may not see it, and `leader` does not exist on that value.
   */
  const isClientTeamLeader =
    MaskedTeam.isPresentNotMasked(workAgreement?.clientTeam) &&
    Boolean(
      workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id) &&
      workAgreement.clientTeam.leader.legalEntityId.isEqualTo(session.user?.legalEntityId),
    );
  const isVendorTeamLeader =
    MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
    Boolean(
      workAgreement.vendorTeam.leader?.userId.isEqualTo(session.user?.id) &&
      workAgreement.vendorTeam.leader.legalEntityId.isEqualTo(session.user?.legalEntityId),
    );

  /**
   * Whether a revision the reader may know about is in flight against this agreement.
   *
   * The frontend keeps no rule of its own here. The server decides who is told about an open
   * revision — an unsubmitted draft is private to the party that opened it — so the id arriving at
   * all is both the permission to draw the banner and the reason Revise is refused. The two answers
   * come from one rule on the server (`OpenWorkAgreementRevisions`), which is what stops a disabled
   * button and a server refusal from disagreeing. The field is re-sent over the work agreement's
   * realtime channel, so the button changes state when the counterparty submits a revision, and
   * changes back when that revision is hired or archived.
   */
  const hasOpenRevision = Boolean(workAgreement?.openRevisionProposalId);

  /** The reader as the organization they act for: the identity every team on the agreement is keyed by. */
  const viewer =
    session.user?.id && session.user.legalEntityId
      ? new EntityMember({ userId: session.user.id, legalEntityId: session.user.legalEntityId })
      : undefined;
  /**
   * The team the reader leads, if either. A leader leaves only by handing the team over (#5836), so
   * Leave Team opens the handover dialog for them instead of quitting.
   */
  const ledSide = isClientTeamLeader ? WorkTeamSide.Client : isVendorTeamLeader ? WorkTeamSide.Vendor : undefined;
  const ledTeam = ledSide === WorkTeamSide.Client ? workAgreement?.clientTeam : workAgreement?.vendorTeam;
  const ledTeamMembers = MaskedTeam.isPresentNotMasked(ledTeam) ? ledTeam.members : [];
  const ledTeamLeader = MaskedTeam.isPresentNotMasked(ledTeam) ? ledTeam.leader : undefined;
  // Nobody leads both sides, so the other side's people are not offered as a successor.
  const otherTeam = ledSide === WorkTeamSide.Client ? workAgreement?.vendorTeam : workAgreement?.clientTeam;
  const otherSideUserIds = MaskedTeam.isPresentNotMasked(otherTeam)
    ? [
        ...(otherTeam.leader ? [otherTeam.leader.userId.value] : []),
        ...otherTeam.members.map((member) => member.userId.value),
      ]
    : [];
  const isVendorTeamMember =
    MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
    workAgreement.vendorTeam.members.some((member) => member.isEqualTo(viewer));
  /** A client team member comes and goes freely; the work always continues (#5836). */
  const isClientTeamMember =
    MaskedTeam.isPresentNotMasked(workAgreement?.clientTeam) &&
    workAgreement.clientTeam.members.some((member) => member.isEqualTo(viewer));
  const pendingLeaderHandovers = workAgreement?.pendingLeaderHandovers ?? [];
  const ledTeamHandoverIsPending = ledSide !== undefined && workAgreement?.pendingLeaderHandoverOf(ledSide) !== undefined;

  // #region Lifecycle
  useEffect(() => {
    if (!workAgreementId || !workAgreementIsNoLongerAvailable) return;

    // Keep the cached agreement while the refetch is pending. Clear it only after the server
    // confirms that this user can no longer read it or that it no longer exists.
    removeWorkAgreementApiCaches({
      dispatch,
      getState: () => store.getState(),
      session,
      workAgreementIdValue: workAgreementId.value,
    });
    dispatch(removeWorkAgreement(workAgreementId));
  }, [dispatch, session, store, workAgreementId, workAgreementIsNoLongerAvailable]);

  /**
   * The agreement the URL names is one the reader cannot open, so say so and go back to the table.
   *
   * The wait is on `isFetching`, not on `isLoading`. RTK Query reports `isLoading` for the first
   * load a hook ever makes and for none after it, so a hook that already holds one agreement reports
   * `isLoading: false` from the first render under a new id -- while it still holds no data for that
   * id at all. The two revision links on this page go from one agreement to another through the same
   * route element, which keeps this page mounted and changes the id under the hook, so the page
   * reported the agreement it had been sent to as missing before it had asked the server for it: the
   * reader saw the "Work Agreement not found" message and the Work Agreements table, and a second
   * click on the same link worked because the store then held the agreement (issue #5633).
   * `isFetching` is true for every request the hook makes, the first and each one after it.
   */
  useEffect(() => {
    // Work Agreement invalid/not found
    const shouldShowNotFound =
      Boolean(workAgreementId) &&
      !workAgreementId?.isEmpty &&
      (workAgreement === null || workAgreementIsNoLongerAvailable) &&
      !workAgreementQuery.isFetching;

    if (shouldShowNotFound && idParam) {
      enqueueSnackbar(
        workAgreementQueryErrorStatus === HttpStatusCode.Forbidden
          ? "You do not have permission to view this work agreement"
          : "Work Agreement not found",
        { variant: "warning" },
      );
      navigate(workAgreementListPath(Boolean(isArchived)), { replace: true });
    }
  }, [
    activeTab,
    idParam,
    navigate,
    workAgreement,
    workAgreementId,
    workAgreementIsNoLongerAvailable,
    workAgreementQueryErrorStatus,
    workAgreementQuery.isFetching,
    isArchived,
  ]);

  useEffect(() => {
    if (workAgreementIsNoLongerAvailable) return;
    if (!workAgreement?.id) return;
    const shouldUseArchivedRoute = Boolean(workAgreement.archiveDate);
    if (shouldUseArchivedRoute === isArchived) return;

    // The query goes along, so a link that opened the pane still opens it on the archived route.
    navigate(
      `/work/${shouldUseArchivedRoute ? "archived" : "active"}/view/${workAgreement.id.value}/${activeTab}${location.search}`,
      { replace: true },
    );
  }, [
    activeTab,
    isArchived,
    location.search,
    navigate,
    workAgreement?.archiveDate,
    workAgreement?.id,
    workAgreementIsNoLongerAvailable,
  ]);

  // Permissions bootstrap
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);
  // #endregion

  // #region Functions
  const messagesPanelIsOpen = isMessageDrawerOverlay ? messagesPanelOpenState.overlay : messagesPanelOpenState.docked;

  // Closing the pane a link opened takes the link's request out of the URL, so a reload shows the
  // page as the reader left it.
  function handleToggleMessagesPanel() {
    if (messagesPanelIsOpen) forgetPaneRequest();
    setMessagesPanelOpenState((prev) => {
      const panelMode = isMessageDrawerOverlay ? "overlay" : "docked";
      return {
        ...prev,
        [panelMode]: !prev[panelMode],
      };
    });
  }

  function handleCloseMessagesPanel() {
    forgetPaneRequest();
    setMessagesPanelOpenState((prev) => {
      const panelMode = isMessageDrawerOverlay ? "overlay" : "docked";
      return {
        ...prev,
        [panelMode]: false,
      };
    });
  }

  // Message threads bootstrap
  useEffect(() => {
    if (!workAgreement || workAgreementQuery.isLoading) return;
    dispatch(populateForumsByTopic({ session, topic }));
  }, [dispatch, session, topic, workAgreement, workAgreementId, workAgreementQuery.isLoading]);

  // Account view switching
  /**
   * The subject this page last moved the account view for. Opening it puts the person in the party
   * they sit on, once; after that the view is theirs. Without this the effect below re-ran on every
   * new session -- including the one the account switcher publishes -- saw the "wrong" party again,
   * and switched straight back: a second PATCH undoing the first.
   */
  const accountViewAlignedFor = useRef<string | undefined | null>(null);
  useEffect(() => {
    if (!workAgreement || accountViewAlignedFor.current === workAgreementId?.value) return;
    accountViewAlignedFor.current = workAgreementId?.value;
    if (
      MaskedTeam.isPresentNotMasked(workAgreement.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(workAgreement.clientTeam) &&
      session.context?.viewingAsVendor &&
      isClientTeamLeader
    ) {
      session.setAccountViewType(AccountType.Client);
    }

    // If viewing as client but user is vendor team member or leader, switch to vendor view
    else if (
      MaskedTeam.isPresentNotMasked(workAgreement.vendorTeam) &&
      !session.context?.viewingAsVendor &&
      (isVendorTeamLeader ||
        workAgreement.vendorTeam?.members?.some(
          (member) =>
            member.userId.isEqualTo(session.user?.id) &&
            member.legalEntityId.isEqualTo(session.user?.legalEntityId),
        ))
    ) {
      session.setAccountViewType(AccountType.Vendor);
    }
  }, [isClientTeamLeader, isVendorTeamLeader, workAgreement, workAgreementId, session]);

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    navigate(`/work/${isArchived ? "archived" : "active"}/view/${workAgreementId?.value}/${newTab}`);
  }

  async function handleEndWorkAgreement() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }

    const response = await confirm({
      title: "End Work Agreement?",
      message:
        "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "End",
    });

    if (response === ConfirmResponse.Cancel) return;

    setActionRecovery(null);
    if (workAgreement.version === undefined) {
      await workAgreementQuery.refetch();
      setActionRecovery({ action: "end", status: "missing-version" });
      return;
    }

    try {
      setIsEnding(true);

      await endWorkAgreement({ workAgreementId: workAgreement.id.value, version: workAgreement.version }).unwrap();
      enqueueSnackbar("Work agreement ended", { variant: "success" });
      navigate(`/work/archived/view/${workAgreement.id.value}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      if (await refreshAfterWriteConflict(error, "end")) return;
      enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "Failed to end work agreement", { variant: "error" });
    } finally {
      setIsEnding(false);
    }
  }

  async function handleReviseWorkAgreement() {
    if (!workAgreement?.id) {
      throw new Error("Work agreement not found");
    }
    if (!session.user) {
      throw new Error("User not found");
    }

    const response = await confirm({
      title: "Revise Work Agreement?",
      message:
        "When these changes are accepted and hired, this work agreement will end at the " +
        "start date of the new agreement.",
      okButtonText: "Revise",
    });

    if (response === ConfirmResponse.Cancel) return;

    setActionRecovery(null);
    if (workAgreement.version === undefined) {
      await workAgreementQuery.refetch();
      setActionRecovery({ action: "revise", status: "missing-version" });
      return;
    }

    try {
      setIsRevising(true);
      const proposalJson = await reviseWorkAgreement({ workAgreement, session }).unwrap();
      if (!proposalJson.id) {
        throw new Error("Invalid proposal response");
      }
      enqueueSnackbar("New revision created", { variant: "success" });
      navigate(`/proposals/active/revise/${proposalJson.id}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      if (await refreshAfterWriteConflict(error, "revise")) return;
      enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "Failed to revise work agreement", { variant: "error" });
    } finally {
      setIsRevising(false);
    }
  }

  async function handleLeaveTeam() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }
    if (!session.user?.id) {
      console.error("User not found");
      return;
    }

    const teamName = isClientTeamMember ? "client team" : "vendor team";
    const response = await confirm({
      title: "Leave Team?",
      message: `Are you sure you want to leave the ${teamName}?`,
      okButtonText: "Leave",
    });

    if (response === ConfirmResponse.Cancel) return;

    setActionRecovery(null);
    if (workAgreement.version === undefined) {
      await workAgreementQuery.refetch();
      setActionRecovery({ action: "quit", status: "missing-version" });
      return;
    }

    try {
      setIsLeavingTeam(true);
      await quitWorkTeam({
        workId: workAgreement.id.value,
        version: workAgreement.version,
        sourceProposalId: workAgreement.sourceProposalId?.value,
      }).unwrap();
      enqueueSnackbar(`You have left the ${teamName}`, { variant: "success" });
    } catch (error) {
      console.error(error);
      if (await refreshAfterWriteConflict(error, "quit")) return;
      enqueueSnackbar(getErrorMessageFromUnknown(error) ?? `Failed to leave the ${teamName}`, { variant: "error" });
    } finally {
      setIsLeavingTeam(false);
    }
  }

  async function handleOfferLeaderHandover(choice: LeaderHandoverChoice) {
    if (!workAgreement?.id || workAgreement.version === undefined) return;
    setActionRecovery(null);
    setIsAnsweringHandover(true);
    const outcome = await requestTeamLeaderHandover({
      workAgreementId: workAgreement.id.value,
      version: workAgreement.version,
      ...choice,
    })
      .unwrap()
      .then(
        () => undefined,
        (error: unknown) => error,
      );
    setIsAnsweringHandover(false);
    if (outcome === undefined) {
      setHandoverDialogOpen(false);
      enqueueSnackbar("The team was offered. Nothing changes until they accept.", { variant: "success" });
      return;
    }
    if (await refreshAfterWriteConflict(outcome, "handover")) {
      setHandoverDialogOpen(false);
      return;
    }
    enqueueSnackbar(getErrorMessageFromUnknown(outcome) ?? "The team could not be offered", { variant: "error" });
  }

  async function handleAnswerLeaderHandover(answer: "accept" | "decline" | "withdraw") {
    if (!workAgreement?.id || workAgreement.version === undefined) return;
    setActionRecovery(null);
    setIsAnsweringHandover(true);
    const args = { workAgreementId: workAgreement.id.value, version: workAgreement.version };
    const outcome = await (
      answer === "accept"
        ? acceptTeamLeaderHandover(args).unwrap()
        : answer === "decline"
          ? declineTeamLeaderHandover(args).unwrap()
          : withdrawTeamLeaderHandover(args).unwrap()
    ).then(
      (continuation) => ({ continuation, error: undefined }),
      (error: unknown) => ({ continuation: null, error }),
    );
    setIsAnsweringHandover(false);
    if (outcome.error !== undefined) {
      if (await refreshAfterWriteConflict(outcome.error, "handover")) return;
      enqueueSnackbar(getErrorMessageFromUnknown(outcome.error) ?? "The handover could not be answered", {
        variant: "error",
      });
      return;
    }
    if (answer === "accept" && outcome.continuation?.id) {
      // The team was restricted, so the work ended: the continuation the successor leads is theirs to
      // negotiate now.
      enqueueSnackbar("You lead the team now. The work ended, and a new proposal is ready to negotiate.", {
        variant: "success",
      });
      navigate(`/proposals/active/revise/${outcome.continuation.id}/details`, { replace: true });
      return;
    }
    enqueueSnackbar(
      answer === "accept"
        ? "You lead the team now."
        : answer === "decline"
          ? "You declined the handover."
          : "You withdrew the handover.",
      { variant: "success" },
    );
    await workAgreementQuery.refetch();
  }

  async function refreshAfterWriteConflict(error: unknown, action: WorkAgreementActionRecovery): Promise<boolean> {
    const status = getWriteStatus(error);
    if (status !== HttpStatusCode.Conflict && status !== HttpStatusCode.PreconditionFailed) return false;

    setActionRecovery({ action, status });
    await workAgreementQuery.refetch();
    return true;
  }

  // Declared after `refreshAfterWriteConflict`, which it is handed, and before the early returns below.
  const removeFromWork = useRemoveFromWork(
    workAgreement,
    isClientTeamLeader,
    (error) => refreshAfterWriteConflict(error, "remove"),
  );

  // #region Render
  // A failed load is never retried on its own, so a spinner here would stay up for good (#6094).
  if (!sessionPermissions && sessionPermissionsError && !sessionPermissionsLoading)
    return (
      <LoaderContainer>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => dispatch(populateSessionPermissions({ session }))}>
              Try again
            </Button>
          }
        >
          {"We couldn't load your permissions, so this work agreement can't be shown."}
        </Alert>
      </LoaderContainer>
    );
  if (!sessionPermissions || isLoading)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToWork) return <NotAuthorized />;
  // Said at once rather than behind a spinner. The effect above also takes the reader back to the
  // list, but only once no request is in flight, and a page that kept refetching never got there.
  if (workAgreementIsNoLongerAvailable)
    return (
      <LoaderContainer>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" component={Link} to={workAgreementListPath(Boolean(isArchived))}>
              Back to work
            </Button>
          }
        >
          {workAgreementQueryErrorStatus === HttpStatusCode.Forbidden
            ? "You do not have permission to view this work agreement."
            : "This work agreement was not found."}
        </Alert>
      </LoaderContainer>
    );

  return (
    // The measured width is published for the tabs, and the scaled type for everything below —
    // including the chat drawer, which is a sibling of the pane rather than a child of it and so
    // takes the theme but not the container. See `work-pane-typography`.
    <WorkPaneWidthContext.Provider value={paneWidth}>
    <ThemeProvider theme={paneTheme}>
    <PageContainer>
      <Main
        ref={paneRef}
        open={messagesPanelIsOpen}
        isMessageDrawerOverlay={isMessageDrawerOverlay}
        showMessageDrawer={!!workAgreement}
      >
        <StickyPageHeader>
          <HeaderContainer>
            <TitleContainer>
              <PageTitle
                variant="h4"
                aria-label={breadcrumbHeadingName(
                  ["Work Agreements"],
                  WorkVersion.nameWithVersion(workAgreement?.name?.value ?? "Loading...", WorkVersion.of(workAgreement)),
                )}
              >
                {/*
                  * The way back is the breadcrumb, not an arrow beside it (#5907). It goes where the
                  * arrow went, to the list the agreement belongs to, and replaces this entry as the
                  * arrow did, so the browser's own back button does not return to the agreement just left.
                  */}
                <PageTitleBreadcrumb
                  label="Work Agreements"
                  to={workAgreementListPath(Boolean(isArchived))}
                  replace
                />
                {workAgreement?.name?.value ?? "Loading..."}
                <WorkVersionChip version={WorkVersion.of(workAgreement)} />
              </PageTitle>
            </TitleContainer>
            {workAgreementAccess.drawer}
            <ButtonContainer>
              <CorrespondenceButton
                // The button holds an icon, and the visible "Correspondence" label below it is a
                // sibling of the button rather than its content. Without a name of its own the
                // button is announced as "button". The same control on the three proposal pages
                // carries this name already — see `draft-proposal-page.tsx` — so a reader who moves
                // from a proposal to the work agreement it becomes now hears one word on both.
                aria-label="Correspondence"
                size="medium"
                color="primary"
                disabled={!workAgreement?.id || !workAgreement}
                onClick={() => handleToggleMessagesPanel()}
              >
                <MessageIcon fontSize="medium" />
              </CorrespondenceButton>
              <CorrespondenceButtonLabel
                variant="button"
                color={!workAgreement?.id || !workAgreement ? "darkgray" : "primary"}
              >
                Correspondence
              </CorrespondenceButtonLabel>
            </ButtonContainer>
            {/* Who holds a grant on the work agreement, for an administrator with Member Permissions
                (#6172): the lock the proposal it came from shows, last in the header as there (#6598). */}
            {workAgreementAccess.mayOpen && workAgreement?.id && (
              <RecordAccessButton
                onClick={() => {
                  if (workAgreement?.id) workAgreementAccess.open(workAgreement.id);
                }}
              />
            )}
          </HeaderContainer>
          <Header>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              allowScrollButtonsMobile
              value={activeTab}
              onChange={handleTabChange}
            >
              <Tab value={PageTab.Details} label={WorkAgreementFieldCategory.Details} />
              <Tab value={PageTab.ClientTeam} label="Client Team" />
              <Tab value={PageTab.Team} label="Vendor Team" />
              <Tab value={PageTab.FeeSchedule} label={WorkAgreementFieldCategory.FeeSchedule} />
              <Tab value={PageTab.Conflicts} label={WorkAgreementFieldCategory.Conflicts} />
              <Tab value={PageTab.Policies} label={WorkAgreementFieldCategory.Policies} />
              <Tab value={PageTab.Discount} label={WorkAgreementFieldCategory.Discount} />
            </TabsContainer>
          </Header>
        </StickyPageHeader>
        {/*
          * Above the action row, not in it. The server decides who is told about an open revision
          * -- an unsubmitted draft is private to the party that opened it -- so the id arriving at
          * all is the permission to show this, exactly as it is for the link to the same proposal
          * on the Details tab. Withheld from an archived agreement, where the sentence would not be
          * true: an agreement that has ended does not run on until anything is hired.
          */}
        <PageNotices>
        {!isArchived && workAgreement?.isActiveWork && workAgreement.openRevisionProposalId && (
          <ReviewRequestedBanner
            severity="info"
            action={
              <Button
                color="inherit"
                component={Link}
                size="small"
                to={`/proposals/active/revise/${workAgreement.openRevisionProposalId.value}/details`}
              >
                Review Revision
              </Button>
            }
          >
            A revision of this agreement is being negotiated. This agreement runs unchanged until
            that revision is hired.
          </ReviewRequestedBanner>
        )}
        {workAgreement?.frozenAt && (
          <Alert severity="info" role="status" data-testid="left-work-banner">
            You left this work on {workAgreement.frozenAt.format("MM/DD/YYYY")}. This is the agreement
            as it was then. Nothing that happened after you left is shown.
          </Alert>
        )}
        {/*
          * A handover of either team waiting for an answer (#5836). The server sends one only to the
          * people it concerns -- the team it changes, and whoever it waits on -- so its arriving at
          * all is the permission to show it.
          */}
        {!isArchived &&
          workAgreement?.isActiveWork &&
          pendingLeaderHandovers.map((handover) => (
            <LeaderHandoverBanner
              key={handover.id.value}
              handover={handover}
              viewer={viewer}
              endsTheWork={handover.side === WorkTeamSide.Vendor && workAgreement.vendorTeamRosterIsAnAgreedTerm}
              busy={isAnsweringHandover}
              onAccept={() => handleAnswerLeaderHandover("accept")}
              onDecline={() => handleAnswerLeaderHandover("decline")}
              onWithdraw={() => handleAnswerLeaderHandover("withdraw")}
            />
          ))}
        {actionRecovery && (
          <Alert severity="warning" role="alert">
            {recoveryActionLabel(actionRecovery.action)} was not completed. {actionRecovery.status === HttpStatusCode.PreconditionFailed
              ? "This work agreement changed since you opened it. It has been refreshed. Review the current agreement, then choose the action again if it is still needed."
              : actionRecovery.status === HttpStatusCode.Conflict
                ? "The work agreement state no longer allows this action. It has been refreshed. Review the current agreement before choosing an action again."
                : "The current work agreement version was unavailable. The agreement has been refreshed. Review it before choosing this action again."}
          </Alert>
        )}
        </PageNotices>
        {!isArchived && workAgreement?.isHired && !workAgreement.isArchived && (
          <ActionsContainer>
            <WorkAgreementActions>
              {workAgreement.isActiveWork && (isClientTeamLeader || isVendorTeamLeader) && (
                <Tooltip
                  enterDelay={600}
                  placement="bottom-start"
                  title={hasOpenRevision ? REVISION_IN_PROGRESS_TOOLTIP : REVISE_TOOLTIP}
                >
                  {/* The span stays: a disabled MUI button fires no pointer events, so a tooltip
                      on the button itself would never open — and the tooltip is the only thing
                      that says why the button is disabled. */}
                  <WorkAgreementActionSpan>
                    <WorkAgreementActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<EditSquareIcon />}
                      loading={isRevising}
                      disabled={hasOpenRevision || isEnding || isRevising || isLeavingTeam}
                      onClick={handleReviseWorkAgreement}
                    >
                      Revise
                    </WorkAgreementActionButton>
                  </WorkAgreementActionSpan>
                </Tooltip>
              )}
              {workAgreement.isActiveWork && (isVendorTeamMember || isClientTeamMember) && (
                <Tooltip
                  title={`Leave the ${isClientTeamMember ? "client" : "vendor"} team. All work done after leaving will not be tracked.`}
                  placement="bottom"
                >
                  <WorkAgreementActionButton
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutIcon />}
                    loading={isLeavingTeam}
                    disabled={isEnding || isRevising || isLeavingTeam}
                    onClick={handleLeaveTeam}
                  >
                    Leave Team
                  </WorkAgreementActionButton>
                </Tooltip>
              )}
              {workAgreement.isActiveWork && ledSide !== undefined && (
                <Tooltip
                  title={
                    ledTeamHandoverIsPending
                      ? "A handover of your team is already waiting for an answer. Withdraw it from the banner above to offer another."
                      : "Hand the team over to somebody on it, and leave or stay on as a member."
                  }
                  placement="bottom"
                >
                  {/* The span keeps the tooltip working while the button is disabled. */}
                  <WorkAgreementActionSpan>
                    <WorkAgreementActionButton
                      variant="outlined"
                      color="error"
                      startIcon={<LogoutIcon />}
                      loading={false}
                      disabled={ledTeamHandoverIsPending || isEnding || isRevising || isLeavingTeam}
                      onClick={() => setHandoverDialogOpen(true)}
                    >
                      Leave Team
                    </WorkAgreementActionButton>
                  </WorkAgreementActionSpan>
                </Tooltip>
              )}
              {(isClientTeamLeader || isVendorTeamLeader) && (
                <Tooltip title="End Work Agreement" placement="bottom">
                  <WorkAgreementActionSpan>
                    <WorkAgreementActionButton
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      loading={isEnding}
                      disabled={isEnding || isRevising || isLeavingTeam}
                      onClick={handleEndWorkAgreement}
                    >
                      End Agreement
                    </WorkAgreementActionButton>
                  </WorkAgreementActionSpan>
                </Tooltip>
              )}
            </WorkAgreementActions>
          </ActionsContainer>
        )}
        {ledSide !== undefined && ledTeamLeader && handoverDialogOpen && (
          <LeaderHandoverDialog
            open
            side={ledSide}
            outgoingLeader={ledTeamLeader}
            members={ledTeamMembers}
            excludedUserIds={otherSideUserIds}
            canGrantSigningAuthority={sessionUser?.canGrantSigningAuthority() ?? false}
            endsTheWork={ledSide === WorkTeamSide.Vendor && workAgreement?.vendorTeamRosterIsAnAgreedTerm}
            hasRevisionInFlight={hasOpenRevision}
            submitting={isAnsweringHandover}
            onSubmit={handleOfferLeaderHandover}
            onCancel={() => setHandoverDialogOpen(false)}
            onEndWorkInstead={() => {
              setHandoverDialogOpen(false);
              void handleEndWorkAgreement();
            }}
          />
        )}
        {/* Flush to the pane's edges: a tab is panels and tables that already carry padding of their
            own, and they are meant to meet the navigation drawer on one side and the chat on the
            other. The prose inside them asks for its own inset — `workPaneProseInset`. */}
        <Content {...paneFlushContent}>
          <TabContent>
            {workAgreement && (
              <>
                {activeTab === PageTab.Details && <DetailsTab workAgreement={workAgreement} />}
                {activeTab === PageTab.ClientTeam && (
                  <ClientTeamTab workAgreement={workAgreement} removeFromWork={removeFromWork[WorkTeamSide.Client]} />
                )}
                {activeTab === PageTab.Team && (
                  <TeamTab
                    entityId={session.user!.legalEntityId}
                    workAgreement={workAgreement}
                    refreshWorkAgreement={workAgreementQuery.refetch}
                    removeFromWork={removeFromWork[WorkTeamSide.Vendor]}
                  />
                )}
                {activeTab === PageTab.FeeSchedule && <FeeScheduleTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Conflicts && <ConflictsTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Policies && <PoliciesTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Discount && <DiscountTab workAgreement={workAgreement} />}
              </>
            )}
          </TabContent>
        </Content>
      </Main>
      <ChatDrawer
        conversationId={workAgreement?.conversationId?.value}
        workName={workAgreement?.name?.value}
        forums={sortedForums}
        isOverlay={isMessageDrawerOverlay}
        isOpen={messagesPanelIsOpen}
        readOnly={isArchived}
        forumRequest={forumRequest}
        onClose={handleCloseMessagesPanel}
      />
    </PageContainer>
    </ThemeProvider>
    </WorkPaneWidthContext.Provider>
  );
}
